import {
  frameRegionInViewer,
  holdFramedRegion,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useZoomControls'
import { CITED_TEXT_FRAMING } from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { unionNormalizedBBoxes } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import { type RefObject, useEffect, useRef } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

interface PreviewCitedTextFrameOptions {
  transformRef: RefObject<ReactZoomPanPinchRef | null>
  boxes: NormalizedBBox[] | undefined
  isFitted: boolean
  minScale: number
  resetToFit: () => void
}

export function usePreviewCitedTextFrame({
  transformRef,
  boxes,
  isFitted,
  minScale,
  resetToFit,
}: PreviewCitedTextFrameOptions): void {
  const hasAppliedFrameRef = useRef(false)
  const landedBoxesRef = useRef<NormalizedBBox[] | null>(null)

  useEffect(() => {
    if (!isFitted) {
      hasAppliedFrameRef.current = false
      landedBoxesRef.current = null
      return
    }
    if (!boxes?.length) {
      if (hasAppliedFrameRef.current) {
        hasAppliedFrameRef.current = false
        landedBoxesRef.current = null
        resetToFit()
      }
      return
    }
    if (landedBoxesRef.current === boxes) return

    const bbox = unionNormalizedBBoxes(boxes)
    return holdFramedRegion(
      (animationTime) =>
        frameRegionInViewer(
          transformRef.current,
          bbox,
          minScale,
          animationTime,
          CITED_TEXT_FRAMING
        ),
      () => transformRef.current?.instance.transformState.scale,
      {
        onApplied: () => {
          hasAppliedFrameRef.current = true
        },
        onSettled: () => {
          landedBoxesRef.current = boxes
        },
      }
    )
  }, [boxes, isFitted, minScale, resetToFit, transformRef])
}
